import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { company } from "@/data/site";

export const Route = createFileRoute("/ada-compliance")({
  head: () => ({
    meta: [
      { title: "ADA Compliance | McKay Roofing & Solar" },
      { name: "description", content: "Our commitment to digital accessibility and ADA compliance for all visitors to our website." },
      { name: "keywords", content: "ADA compliance, accessibility, WCAG, McKay Roofing & Solar" },
      { property: "og:title", content: "ADA Compliance – McKay Roofing & Solar" },
      { property: "og:description", content: "Our commitment to digital accessibility." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/ada-compliance" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/ada-compliance" }],
  }),
  component: ADAPage,
});

function ADAPage() {
  const updated = "July 2026";
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <h1 className="text-5xl font-extrabold sm:text-6xl text-balance">ADA Compliance</h1>
            <p className="mt-4 text-primary-foreground/80">Last updated: {updated}</p>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8 prose prose-slate max-w-none">
          <h2 className="mt-4 text-2xl font-bold">Our commitment to accessibility</h2>
          <p>
            McKay Roofing &amp; Solar is committed to ensuring digital accessibility for people with
            disabilities. We are continually improving the user experience for everyone and applying
            the relevant accessibility standards to help make our website usable for all visitors.
          </p>

          <h2 className="mt-8 text-2xl font-bold">Conformance status</h2>
          <p>
            We aim to conform with the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA. These
            guidelines explain how to make web content more accessible for people with a wide range of
            disabilities and more user-friendly for everyone.
          </p>

          <h2 className="mt-8 text-2xl font-bold">Measures we take</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Semantic HTML structure and clear headings for screen readers.</li>
            <li>Alt text on meaningful images.</li>
            <li>Keyboard-navigable menus, forms, and interactive elements.</li>
            <li>Sufficient color contrast between text and background.</li>
            <li>Descriptive link and button labels.</li>
            <li>Ongoing testing and improvements as our site evolves.</li>
          </ul>

          <h2 className="mt-8 text-2xl font-bold">Feedback &amp; assistance</h2>
          <p>
            If you encounter an accessibility barrier on our website, or need information in an
            alternative format, please contact us and we will work with you to provide the information
            or service you need.
          </p>
          <p>
            <a href={company.phoneHref} className="text-primary underline">{company.phone}</a><br />
            <a href={`mailto:${company.email}`} className="text-primary underline">{company.email}</a>
          </p>

          <h2 className="mt-8 text-2xl font-bold">Ongoing effort</h2>
          <p>
            Accessibility is an ongoing effort. We welcome your feedback and will make reasonable
            efforts to address concerns promptly.
          </p>
        </section>
        <CTABlock title="Questions about accessibility on our site?" subtitle="Reach out any time – or get a free roofing and solar quote while you're here." />
      </main>
      <SiteFooter />
    </div>
  );
}